import * as React from "react"
import { mergeProps } from "@base-ui/react/merge-props"
import { useRender } from "@base-ui/react/use-render"

import { cn } from "@/lib/utils"

/**
 * A shadcn Typography skála (https://ui.shadcn.com/docs/components/base/typography) komponensként,
 * az osztályok a dokumentációból változtatás nélkül. Csak a betű van benne (méret, súly, ritkítás,
 * sorköz, szín); a dokumentum-ritmust adó margókat (`mt-10`, `[&:not(:first-child)]:mt-6`) és a h2
 * alsó vonalát az elrendezés (`gap`, `space-y`) adja, mert az app felületein ezek duplázódnának.
 * Betű: a H1 és a H2 a márka díszbetűje (`--font-display` = a kiszélesített Archivo, ezért kapnak
 * `font-stretch-*` osztályt is), a H3/H4 a felület címbetűje (`--font-heading`, ma az Inter).
 * Mindhárom token az `index.css`-ben él, betűt cserélni csak ott kell.
 *
 * A vizuális szint és a HTML elem a `render` proppal választható szét (Base UI konvenció):
 * `<H2 render={<h1 />}>Foglalásaim</H2>` – oldalcím `h1` elemként, a H2 méretében.
 */

function typography<T extends keyof React.JSX.IntrinsicElements>(
  tag: T,
  slot: string,
  classes: string
) {
  function Typography({ className, render, ...props }: useRender.ComponentProps<T>) {
    return useRender({
      defaultTagName: tag,
      props: mergeProps({ className: cn(classes, className) }, props),
      render,
      state: { slot },
    })
  }
  Typography.displayName = slot
  return Typography
}

/** Hero cím (nyitóoldal, belépés képes oldala). A legszélesebb, legsúlyosabb hang. */
const H1 = typography(
  "h1",
  "typography-h1",
  "scroll-m-20 font-display font-stretch-expanded text-4xl font-extrabold tracking-tight text-balance"
)

/**
 * Oldalcím (`render={<h1 />}`), nyitóoldali szekciócím. Ugyanaz a díszbetű, de egy fokkal
 * keskenyebben és nehezebben: minden oldal címe ez, a hosszú magyar címeknek kell a hely.
 */
const H2 = typography(
  "h2",
  "typography-h2",
  "scroll-m-20 font-display font-stretch-semi-expanded text-3xl font-bold tracking-tight"
)

/** Oldalon belüli szekció címe, státusz-cím, kiemelt szám (`render={<p />}`). */
const H3 = typography(
  "h3",
  "typography-h3",
  "scroll-m-20 font-heading text-2xl font-semibold tracking-tight"
)

/** Alszekció, lépés vagy blokk címe. */
const H4 = typography(
  "h4",
  "typography-h4",
  "scroll-m-20 font-heading text-xl font-semibold tracking-tight"
)

/** Többsoros folyószöveg oldal szintjén (nem kártyában). */
const P = typography("p", "typography-p", "leading-7")

/** Bevezető sor cím alatt. */
const Lead = typography("p", "typography-lead", "text-xl text-muted-foreground")

/** Kiemelt érték: végösszeg, számláló, pályaszám. */
const Large = typography("div", "typography-large", "text-lg font-semibold")

/** Címke-szerű rövid szöveg (oszlopcím, felirat). */
const Small = typography("small", "typography-small", "text-sm leading-none font-medium")

/** Másodlagos, magyarázó szöveg. */
const Muted = typography("p", "typography-muted", "text-sm text-muted-foreground")

/** Azonosító, kód (foglalás- és rendelésazonosító). */
const InlineCode = typography(
  "code",
  "typography-inline-code",
  "relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold"
)

export { H1, H2, H3, H4, P, Lead, Large, Small, Muted, InlineCode }
